import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { clearAccountScopedStorage } from '@/lib/accountScopedStorage';
import { useEffectiveScopeId } from '@/hooks/useEffectiveScopeId';

export const AccountScopeBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const selectedAccountId = useEffectiveScopeId();
  const location = useLocation();
  const navigate = useNavigate();

  // `generation` keys the page tree. It only advances on a real switch — from
  // one scope to another, or to none — so page state never leaks across
  // workspaces. The first adoption (null → id) is not a switch: on every
  // reload the scope reads null until the workspace list lands, and treating
  // that as a switch blanked the page for a frame and remounted it, restarting
  // its loading state after it had already painted.
  const [scope, setScope] = React.useState<{ id: string | null; generation: number }>({
    id: selectedAccountId,
    generation: 0,
  });
  const scopeId = scope.id;
  const isSwitching = selectedAccountId !== scopeId;
  const isInitialAdoption = isSwitching && scopeId === null;

  const preSwitchLocationKeyRef = React.useRef(location.key);
  if (!isSwitching) {
    preSwitchLocationKeyRef.current = location.key;
  }

  React.useLayoutEffect(() => {
    if (!isSwitching) return;

    const navigatedWithSwitch = location.key !== preSwitchLocationKeyRef.current;

    if (scopeId !== null && !navigatedWithSwitch) {
      clearAccountScopedStorage();
      if (location.search || location.hash) {
        navigate(location.pathname, { replace: true });
        return;
      }
    }
    setScope((prev) => ({
      id: selectedAccountId,
      generation: prev.id === null ? prev.generation : prev.generation + 1,
    }));
  }, [isSwitching, scopeId, selectedAccountId, location, navigate]);

  if (isSwitching && !isInitialAdoption) return null;

  return <React.Fragment key={scope.generation}>{children}</React.Fragment>;
};

export default AccountScopeBoundary;
